import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import {
  anyGuidedSectionUnconfirmed,
  getSectionStatus,
  SECTION_LABEL,
  SECTION_ORDER,
  SECTIONS_UNCONFIRMED_RULE,
  validateEFilingOrder,
} from '@lds-efsp/shared-contracts';
import { EFilingProvider, useEFiling, type EFilingTab } from '../contexts/EFilingContext';
import { resolveEntryIntent, hasFreshStartSignal, FRESH_START_PARAM } from '../lib/resolve-entry-intent';
import { isConfirmFixableBlocker } from '../lib/confirm-fixable-blockers';
import {
  blockerItemId,
  CANONICAL_BLOCKER_SURFACE,
  resolveOffendingFieldId,
} from '../lib/offending-fields';
import { BlockingMessage, type BlockingSurface } from './BlockingMessage';
import OrderInfoTab from './OrderInfoTab';
import CaseDetailsTab from './CaseDetailsTab';
import ConfirmFilingTab from './ConfirmFilingTab';
import DocumentsTab from './DocumentsTab';
import CourtFeesTab from './CourtFeesTab';
import OrderDetailsTab from './OrderDetailsTab';
import SummaryV2 from './SummaryV2';
import HelpDrawer from './HelpDrawer';
import WizardBreadcrumbs from './WizardBreadcrumbs';
import WizardHero from './WizardHero';
import WizardStepper from './WizardStepper';

interface EFilingWizardProps {
  brand: BrandConfig;
}

interface TabDef {
  id: EFilingTab;
  label: string;
}

// Keep these id orders in sync with the TAB_ORDER constants in EFilingContext.tsx — navigation and the stepper drift apart otherwise.
const CLASSIC_TABS: TabDef[] = [
  { id: 'documents', label: 'Upload Documents' },
  { id: 'order-info', label: 'Where to File' },
  { id: 'case-details', label: 'About Your Case' },
  { id: 'court-fees', label: 'Review Fees' },
  { id: 'order-details', label: 'Submit' },
];

// Consolidated flow (Walter R49 phase 1, ?confirmFlow=1): Where to File +
// About Your Case merge into the single Confirm Your Filing step.
const CONFIRM_TABS: TabDef[] = [
  { id: 'documents', label: 'Upload Documents' },
  { id: 'confirm', label: 'Confirm Your Filing' },
  { id: 'court-fees', label: 'Review Fees' },
  { id: 'order-details', label: 'Submit' },
];

// Presentation for the Next-adjacent blocker list (WOL-433 / Walter R77/R78,
// confirm flow): per validation rule, an optional plain-language fix hint and
// the section to scroll to. Rules without an entry still list their message.
const NEXT_BLOCKER_PRESENTATION: Record<string, { hint?: string; scrollTarget?: string }> = {
  'lead-formgroup': {
    hint: 'Choose an eligible initiating document (like a Complaint or Petition) as the Lead Document.',
    scrollTarget: 'document-upload-section',
  },
  'lead-document-required': { scrollTarget: 'document-upload-section' },
  'single-lead-document': { scrollTarget: 'document-upload-section' },
  'doc-type-required': { scrollTarget: 'document-upload-section' },
  'doc-formgroup-ci': { scrollTarget: 'document-upload-section' },
  'fee-waiver-requires-fw001': { scrollTarget: 'document-upload-section' },
  'org-plaintiff-requires-attorney': { scrollTarget: 'attorney-section' },
  // R90 (WOL-442): Upload foundation requirements — each jumps to the in-card
  // selector that satisfies it on this step.
  'upload-court-required': { scrollTarget: 'ocr-confirm-cards' },
  'upload-case-category-required': { scrollTarget: 'ocr-confirm-cards' },
  'upload-case-type-required': { scrollTarget: 'ocr-confirm-cards' },
  // R86/R87 (WOL-443): existing-case linkage — both jump to the case lookup in
  // the Court section, which fills category/type and the participant roster.
  'case-attach-required': {
    hint: 'Search by case number in the Court section — the case fills in its category, type, and participants for you.',
    scrollTarget: 'case-search-section',
  },
  'subcase-required': { scrollTarget: 'case-search-section' },
  // SOR-162: an attached case whose county no longer matches the selected
  // county blocks Next — surface it adjacent to the control with a jump to the
  // Upload-step case lookup, where the filer detaches or re-searches in place.
  'case-county-mismatch': {
    hint: 'Change the county back to that court, or use "Change court/case" to search in the current county.',
    scrollTarget: 'case-search-upload',
  },
  // R29 (SPEC-SOR-174): the attached case carries no court binding we can
  // attribute to the case record, so the only fix is re-running the lookup
  // under the current county — same jump target as the mismatch above, since
  // that lookup is what re-attaches the case with a recorded court.
  'case-binding-absent': {
    hint: 'Use "Change court/case" to search for the case again in the current county — that records which court it belongs to.',
    scrollTarget: 'case-search-upload',
  },
  // R103 (WOL-447): unlawful detainer requires the defendant's mailing
  // address on the party record — captured in the party's Edit modal.
  'ud-defendant-address-required': {
    hint: 'Open the defendant in Case Participants (Edit) and fill in the Mailing Address block.',
    scrollTarget: 'participants-section',
  },
  // R10 (SPEC-SOR-174): each guided section's confirm control is the fix for
  // its own blocker, so each jumps straight to that control. Every target is on
  // the step that owns the section (AX3) — the Upload pair to the Upload step,
  // the Confirm pair to the Confirm step — which is the R21 no-deadlock rule
  // read off the presentation rather than merely off the validator.
  'upload-court-section-required': { scrollTarget: 'section-confirm-sec_court' },
  'upload-case-section-required': { scrollTarget: 'section-confirm-sec_case' },
  'confirm-parties-section-required': { scrollTarget: 'section-confirm-sec_parties' },
  'confirm-documents-section-required': { scrollTarget: 'section-confirm-sec_documents' },
};

// SPEC-SOR-321 R7: a gated control is no longer HTML-disabled — it stays
// activatable, and an activation while gated refuses and moves focus onto the
// first POPULATED blocking-message container, so the reason is reachable
// without hunting. The order is pinned by the spec: the list adjacent to the
// activated control first (next-blockers), then the step's primary validation
// banner, then the fee gate. Typed as the container's own surface ids, so a
// rename at BlockingMessage is a compile error here rather than a silently
// unfocusable region.
const BLOCKING_FOCUS_ORDER: readonly BlockingSurface[] = [
  'next-blockers',
  'validation-banner',
  'fee-gate-blockers',
];

// The focus decision rides beside the two gated handlers that consult it (same
// precedent as liveRoleForSurface beside BlockingMessage): a second module
// would let the refusal's target drift from the containers the wizard renders.
// eslint-disable-next-line react-refresh/only-export-components
export function firstPopulatedBlockingId(
  isPopulated: (id: BlockingSurface) => boolean,
): BlockingSurface | null {
  for (const id of BLOCKING_FOCUS_ORDER) {
    if (isPopulated(id)) return id;
  }
  return null;
}

const WizardContent: React.FC<{ brand: BrandConfig }> = ({ brand }) => {
  const navigate = useNavigate();
  const [showNewOrderWarning, setShowNewOrderWarning] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const {
    currentTab,
    setCurrentTab,
    showCourtFeesTab,
    confirmFlowEnabled,
    tabValidation,
    getTabValidationErrors,
    getFeeCalcBlockers,
    docTypeMetadata,
    completeness,
    nextTab,
    previousTab,
    saveDraft,
    loadDraft,
    loadDraftFromServer,
    startFreshDraft,
    orderId,
    isSubmitting,
    submitError,
    submitOutcomeUnknown,
    createDraftOrder,
    saveDraftToServer,
    submitOrder,
    clearDraft,
    allUploadsComplete,
    wizardState,
    saveIndicator,
  } = useEFiling();

  const currentTabErrors = getTabValidationErrors(currentTab);
  const currentTabValid = tabValidation[currentTab];

  // Resolve the draft on mount via the R1 entry-intent contract (SOR-293/-294).
  // `edit` (?orderId=) hydrates from the server — the source of truth for saved
  // drafts. `fresh-start` (no orderId, fresh-start signal present) resets to the
  // unbound default and overwrites the persisted slot, then strips the consumed
  // signal from the URL so a reload re-resolves as `resume` (R7). `resume` (no
  // orderId, no signal) restores the localStorage draft (same-browser resume,
  // surviving a page reload or the Auth0 re-auth redirect round-trip).
  const [searchParams, setSearchParams] = useSearchParams();
  const [draftLoadError, setDraftLoadError] = useState<string | null>(null);
  const draftLoadStartedRef = useRef(false);
  useEffect(() => {
    if (draftLoadStartedRef.current) return;
    draftLoadStartedRef.current = true;
    const urlHasOrderId = Boolean(searchParams.get('orderId'));
    const intent = resolveEntryIntent({
      urlHasOrderId,
      freshSignalPresent: hasFreshStartSignal(searchParams),
    });
    if (intent === 'edit') {
      loadDraftFromServer(searchParams.get('orderId')!)
        .then((loaded) => {
          if (!loaded) setDraftLoadError('This order is no longer an editable draft.');
        })
        .catch((err: unknown) => {
          setDraftLoadError(err instanceof Error ? err.message : 'Failed to load your draft.');
        });
    } else if (intent === 'fresh-start') {
      startFreshDraft();
      // Strip the consumed signal so a reload / Auth0 re-auth round-trip re-reads
      // a clean URL and re-resolves as `resume`, never a second fresh-start (R7).
      const next = new URLSearchParams(searchParams);
      next.delete(FRESH_START_PARAM);
      setSearchParams(next, { replace: true });
    } else {
      loadDraft();
    }
  }, [searchParams, setSearchParams, loadDraft, loadDraftFromServer, startFreshDraft]);

  const visibleTabs = (confirmFlowEnabled ? CONFIRM_TABS : CLASSIC_TABS).filter(
    t => t.id !== 'court-fees' || showCourtFeesTab,
  );

  const currentTabIndex = visibleTabs.findIndex(t => t.id === currentTab);

  // Track save message for breadcrumb autosave indicator
  // (also used by Save As Draft button below)
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === visibleTabs.length - 1;

  // Fee-calc entry gate (Walter 2026-07-10 R7): when the NEXT step is Review
  // Fees, an incomplete envelope (type-less document, missing required
  // metadata, no lead, …) must not advance — fee calculation would send it to
  // the EFM and bounce as a raw court fault. Blockers are strict documents-step
  // errors, each naming the offending document/field; they can be non-empty
  // even though the CURRENT step is valid, so they gate Next independently.
  const nextTabId = visibleTabs[currentTabIndex + 1]?.id;
  const feeCalcBlockers = nextTabId === 'court-fees' ? getFeeCalcBlockers() : [];
  const nextDisabled = !currentTabValid || feeCalcBlockers.length > 0;

  // R11 (SPEC-SOR-174): Submit additionally waits on every guided section
  // being confirmed. The completeness percentage cannot stand in for it —
  // completeness counts FIELDS, and a draft can reach 100% with all four
  // sections carrying no filer act at all, which is exactly the draft R30 has
  // the shared validator refuse. The two terms answer different questions, so
  // the section term must survive any future completeness change.
  //
  // Hoisted to one const because the disabled state is read three times below
  // (the attribute plus two style expressions) and a term added to only some
  // of them renders a button that looks enabled and refuses to act.
  //
  // R12 (SPEC-SOR-174): Submit additionally follows the SHARED validator's
  // verdict, so the client gate and `validateEFilingOrder` can never disagree
  // and no combination of completeness and section state enables submission
  // over an order the validator has rejected. Completeness answers "how much of
  // the draft is filled in"; the validator answers "may this be filed" — the
  // foreign-court binding (R25) and the absent binding (R29) are both rejected
  // there while a draft can still read 100% complete, which is exactly the
  // disagreement this term closes.
  //
  // Confirm-flow gated, matching R12's own antecedent (`confirm_flow(d) and not
  // order_valid(d) -> not submit_enabled(d)`) and the rule every guided-section
  // behaviour follows: new behaviour is gated at the site, so the classic
  // flow's Submit is left exactly as it ships (R16). The absent-binding half of
  // the verdict is likewise confirm-flow-only inside the validator, keyed on
  // `confirmFlowActive`, so the two halves of the gate agree by construction.
  //
  // Memoized on the state it reads: `validateEFilingOrder` runs every validator
  // in the order, and `submitDisabled` is recomputed on every render of this
  // component (including ones driven by local UI state like the help drawer).
  const orderValidation = useMemo(
    () => (confirmFlowEnabled ? validateEFilingOrder(wizardState) : null),
    [confirmFlowEnabled, wizardState],
  );
  const orderValid = orderValidation?.valid ?? true;

  const sectionsUnconfirmed = confirmFlowEnabled && anyGuidedSectionUnconfirmed(wizardState);

  const submitDisabled =
    isSubmitting ||
    submitOutcomeUnknown ||
    completeness.percentage < 100 ||
    !allUploadsComplete ||
    // Kept alongside the validator term rather than folded into it: this term
    // also drives the `submit-sections-unconfirmed` indicator below, and the two
    // must not be able to drift into a button that looks enabled and refuses.
    sectionsUnconfirmed ||
    !orderValid;

  // R12's indicator half. Every other `submitDisabled` term states itself next
  // to the button — completeness has its remaining-fields span, the section
  // term has `submit-sections-unconfirmed` — so a validator-only refusal was
  // the one way left to render a greyed Submit with no reason on screen. That
  // residue is reachable rather than hypothetical, and it is the whole point of
  // the `!orderValid` term: neither the foreign-court binding (R25) nor the
  // absent one (R29) is a field completeness counts, so such a draft reads 100%
  // complete with every section confirmed while the order is invalid.
  //
  // Scoped to exactly that residue rather than to `!orderValid` alone. On an
  // incomplete draft the validator emits one error per unfilled field, and
  // rendering those here would bury the two messages this indicator exists to
  // surface under a wall the remaining-fields span already summarizes. The
  // section rule is dropped for the same reason — its own indicator sits beside
  // this one, and repeating it would be two spans for one refusal.
  const submitValidatorReasons = useMemo(() => {
    if (orderValid || sectionsUnconfirmed) return [];
    if (completeness.percentage < 100 || !allUploadsComplete) return [];
    return [
      ...new Set(
        (orderValidation?.errors ?? [])
          .filter(e => e.rule !== SECTIONS_UNCONFIRMED_RULE)
          .map(e => e.message),
      ),
    ];
  }, [orderValid, sectionsUnconfirmed, completeness.percentage, allUploadsComplete, orderValidation]);

  // R7's refusal target. A container counts as POPULATED when it has list
  // items — BlockingMessage renders its children only while populated, so an
  // empty region carries no <li> to find. A gated click with no populated
  // region (Submit held by isSubmitting, submitOutcomeUnknown, completeness or
  // upload state — terms that render into none of the three containers)
  // refuses and moves focus nowhere.
  const focusFirstBlockingMessage = () => {
    const target = firstPopulatedBlockingId((id) => {
      const el = document.getElementById(id);
      return el !== null && el.querySelector('li') !== null;
    });
    if (target === null) return;
    // BlockingMessage carries tabIndex={-1}: focusable programmatically, out of
    // the tab order.
    document.getElementById(target)?.focus();
  };

  // R15 (SPEC-SOR-376): a submit failure that leaves the filer on the Submit
  // step renders into the SAME always-mounted `validation-banner` container as
  // the step's validation errors, so a screen-reader user who submits a filing
  // and fails is told what happened instead of being left on the document body.
  //
  // Step-scoped, and not cosmetically: `submitError` is context state that
  // survives tab navigation — the context clears it only at the start of the
  // next attempt and in `clearDraft` — so an unscoped render would carry the
  // failure into every earlier step's banner after the filer walks Back.
  //
  // `currentTabErrors` is always empty on this step (`validateTab('order-details')`
  // emits no error for any state — SPEC-SOR-343 R19), so the two contents are
  // mutually exclusive here by construction: the failure can never displace the
  // per-step <ul> whose <li> ids are the R8 `aria-describedby` targets.
  const submitFailureShown = Boolean(submitError) && currentTab === 'order-details';

  // R15's focus move. An effect rather than a call in the Submit catch: the
  // catch is a promise continuation, so the `setSubmitError` queued inside
  // `submitOrderToApi` has not committed when it runs and a closure read of
  // `submitError` there always sees the pre-attempt `null`. The effect reads
  // the POST-commit truth instead.
  //
  // One-shot per distinct message, keyed on the message itself: an unchanged
  // `submitError` re-rendered by an unrelated state change (the help drawer, a
  // save-indicator tick) must not yank focus again, and a filer who walks Back
  // and forward again must not be re-focused — both are the same string, so the
  // ref already holds it. A NEW failure re-arms it: every attempt clears
  // `submitError` to null before it starts, which resets the ref.
  //
  // An in-doubt outcome never reaches here at all, and needs no carve-out: in
  // `submitOrderToApi` a failed attempt sets `submitError` OR (when the
  // reconcile never reached the server) `submitOutcomeUnknown` — the two arms
  // of one branch, never both — and a submit that RESOLVES sets neither, so
  // `submitError` non-null is exactly the fault set.
  //
  // The same by-id move as `focusFirstBlockingMessage` above, against the
  // `tabIndex={-1}` BlockingMessage carries — but a separate trigger, and
  // deliberately so: that helper's populated test looks for a list item, and a
  // submit failure is a single message, not a list.
  const focusedSubmitErrorRef = useRef<string | null>(null);
  useEffect(() => {
    // No dependency array: the effect must see whichever commit carries the
    // failure, and the ref keeps it inert on every other render.
    if (!submitError) {
      focusedSubmitErrorRef.current = null;
      return;
    }
    if (focusedSubmitErrorRef.current === submitError) return;
    // Consumed BEFORE the gate below, so a failure that lands while the filer is
    // on another step never focuses on a later return to Submit.
    focusedSubmitErrorRef.current = submitError;
    // The failure renders only on the Submit step, so that is the only step
    // whose banner may receive focus for it.
    if (currentTab !== 'order-details') return;
    // BlockingMessage carries tabIndex={-1}: focusable programmatically, out of
    // the tab order.
    document.getElementById('validation-banner')?.focus();
  });

  // "In-progress draft" for the R24 discard gate: anything the filer would
  // actually lose — a bound server order, uploads, roster entries, or the
  // court/case fields that seed the rest of the wizard.
  const draftHasContent =
    Boolean(orderId) ||
    wizardState.documents.length > 0 ||
    wizardState.parties.length > 0 ||
    wizardState.attorneys.length > 0 ||
    Boolean(wizardState.orderInfo.county) ||
    Boolean(wizardState.caseInfo.caseTitle);

  const renderTab = () => {
    switch (currentTab) {
      case 'order-info':
        return <OrderInfoTab brand={brand} />;
      case 'case-details':
        return <CaseDetailsTab brand={brand} />;
      case 'confirm':
        return <ConfirmFilingTab brand={brand} />;
      case 'documents':
        return <DocumentsTab brand={brand} />;
      case 'court-fees':
        return <CourtFeesTab brand={brand} />;
      case 'order-details':
        return <OrderDetailsTab brand={brand} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 120px)' }}>
      {/* Breadcrumbs bar */}
      <WizardBreadcrumbs saveIndicator={saveIndicator} />

      {/* Hero section */}
      <WizardHero currentTab={currentTab} />

      {/* Stepper */}
      <WizardStepper
        brand={brand}
        currentTab={currentTab}
        tabValidation={tabValidation}
        showCourtFeesTab={showCourtFeesTab}
        confirmFlowEnabled={confirmFlowEnabled}
        onStepClick={setCurrentTab}
      />

      {/* Main content area */}
      <div className="flex gap-6 p-6">
        <div className="flex-1">
          {/* Header with Start New Order — the step's primary action (Walter
              R23): filled and high-contrast, visually distinct from the
              secondary/utility controls on the step. Discarding is gated on an
              explicit confirmation ONLY when there is an in-progress draft to
              lose (R24) — a pristine draft resets silently. */}
          <div className="flex justify-between items-center mb-4">
            {/* Walter EARS-13 REQ-13.2: the flow is chosen per page load by the
                URL alone, so a non-default flow must SAY SO on screen —
                otherwise a tester running both flows in one session can
                attribute a result to the wrong one (Walter only caught the
                stale opt-out because the Confirm buttons were missing). The
                default confirm flow renders nothing. */}
            {confirmFlowEnabled ? (
              <div />
            ) : (
              <span
                data-testid="flow-indicator"
                className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border bg-yellow-50 border-yellow-300 text-yellow-800"
              >
                Classic flow
              </span>
            )}
            {showNewOrderWarning ? (
              <div
                data-testid="discard-draft-confirm"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border bg-yellow-50 border-yellow-300"
              >
                <span className="text-yellow-800">
                  Start a new order? Your current draft will be discarded.
                </span>
                <button
                  onClick={() => { clearDraft(); setShowNewOrderWarning(false); }}
                  className="px-2.5 py-1 rounded bg-yellow-500 text-white hover:bg-yellow-600"
                >
                  Discard draft
                </button>
                <button
                  onClick={() => setShowNewOrderWarning(false)}
                  className="px-2.5 py-1 rounded border border-yellow-400 text-yellow-700 hover:bg-yellow-100"
                >
                  Keep working
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  if (draftHasContent) {
                    setShowNewOrderWarning(true);
                  } else {
                    clearDraft();
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition-opacity hover:opacity-90"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                <span aria-hidden="true" className="text-base font-bold leading-none">+</span>
                Start New Order
              </button>
            )}
          </div>

          {/* Draft resume failure banner */}
          {draftLoadError && (
            <div
              role="alert"
              className="mb-4 p-3 rounded-lg border"
              style={{
                backgroundColor: '#FEF2F2',
                borderColor: '#FECACA',
                color: '#991B1B',
              }}
            >
              <p className="text-sm font-semibold mb-1">Couldn't load your draft</p>
              <p className="text-sm">
                {draftLoadError} Go back to{' '}
                <button
                  type="button"
                  className="underline"
                  style={{ color: 'inherit' }}
                  onClick={() => navigate('/orders')}
                >
                  your orders
                </button>{' '}
                and try again, or start a new order.
              </p>
            </div>
          )}

          {/* Validation errors banner — mounted on EVERY step with its role from
              mount (SPEC-SOR-321 R5/R6); it lists the unmet requirements only
              while populated, and paints nothing while empty.

              R15 (SPEC-SOR-376): a submit failure that lands while the filer is
              still on the Submit step renders here too, so the reason is
              announced and not merely drawn. It is the SAME container — no
              second live region is created for it, and no `role` is attached in
              the render that fills it; BlockingMessage keeps carrying its role
              from mount. */}
          <BlockingMessage
            surface="validation-banner"
            populated={currentTabErrors.length > 0 || submitFailureShown}
            heading={
              <p className="text-sm font-semibold mb-1">
                {submitFailureShown
                  ? 'Your filing could not be submitted:'
                  : 'Fix the following before continuing:'}
              </p>
            }
            className="mb-4 p-3 rounded-lg border"
            style={{
              backgroundColor: '#FEF2F2',
              borderColor: '#FECACA',
              color: '#991B1B',
            }}
          >
            {submitFailureShown ? (
              // R15: the submit failure is ONE reason, so it renders as a
              // single paragraph rather than a list item — the same shape the
              // message has, and the shape the focus move above is written for.
              <p className="text-sm">{submitError}</p>
            ) : (
              <ul className="list-disc list-inside text-sm space-y-0.5">
                {currentTabErrors.map((err) => {
                  const scrollTarget =
                    err.rule === 'org-plaintiff-requires-attorney'
                      ? 'attorney-section'
                      : err.rule === 'fee-waiver-requires-fw001'
                        ? 'document-upload-section'
                        : null;
                  return (
                    // R8: the item id an `aria-describedby` names — banner-scoped
                    // via CANONICAL_BLOCKER_SURFACE, the same builder
                    // `offendingFieldAria` builds its pointer from, so the
                    // reference cannot drift to another surface's copy of the
                    // same item.
                    <li key={`${err.field}-${err.rule}`} id={blockerItemId(CANONICAL_BLOCKER_SURFACE, err)}>
                      {scrollTarget ? (
                        <button
                          type="button"
                          className="underline text-left"
                          style={{ color: 'inherit' }}
                          onClick={() => {
                            document
                              .getElementById(scrollTarget)
                              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                        >
                          {err.message}
                        </button>
                      ) : (
                        err.message
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </BlockingMessage>

          {/* Tab content */}
          <div className="mb-6">
            {renderTab()}
          </div>

          {/* Fee-calc entry gate (R7): the next step calculates fees against the
              court, so every document must be complete first. Names each gap in
              plain language. R84 (confirm flow): a blocker satisfiable in the
              Document Assignments section ABOVE (participant-class metadata)
              must not be remedied by a backward link — it gets an in-page jump;
              only Upload-only gaps (doc types, file issues, non-participant
              metadata) keep the Upload Documents link. Classic flow keeps the
              single Upload link (shared surface, post-session cleanup).
              Mounted whenever the next step is Review Fees, empty or not
              (SPEC-SOR-321 R5/R6). */}
          {nextTabId === 'court-fees' && (
            <BlockingMessage
              surface="fee-gate-blockers"
              populated={feeCalcBlockers.length > 0}
              heading={
                <p className="text-sm font-semibold mb-1">
                  Finish your documents before we calculate fees:
                </p>
              }
              className="mb-4 p-3 rounded-lg border"
              style={{
                backgroundColor: '#FFFBEB',
                borderColor: '#FDE68A',
                color: '#92400E',
              }}
            >
              <ul className="list-disc list-inside text-sm space-y-0.5">
                {feeCalcBlockers.map((err) => (
                  <li key={`${err.field}-${err.rule}`}>{err.message}</li>
                ))}
              </ul>
              <div className="mt-2 flex flex-wrap gap-4">
                {confirmFlowEnabled &&
                  feeCalcBlockers.some(e => isConfirmFixableBlocker(e, docTypeMetadata)) && (
                    <button
                      type="button"
                      data-testid="fee-gate-jump-assignments"
                      onClick={() =>
                        document
                          .querySelector('[data-testid="document-assignment-section"]')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }
                      className="text-sm font-medium underline"
                      style={{ color: '#92400E' }}
                    >
                      Set these in Document Assignments above
                    </button>
                  )}
                {(!confirmFlowEnabled ||
                  feeCalcBlockers.some(e => !isConfirmFixableBlocker(e, docTypeMetadata))) && (
                  <button
                    type="button"
                    onClick={() => setCurrentTab('documents')}
                    className="text-sm font-medium underline"
                    style={{ color: '#92400E' }}
                  >
                    Go to Upload Documents
                  </button>
                )}
              </div>
            </BlockingMessage>
          )}

          {/* Next-adjacent blockers (WOL-433 / Walter R76/R77, confirm flow):
              the "Fix the following" box up top can be scrolled off-screen from
              the Next button, so a disabled Next read as a dead stall. List the
              specific unmet requirements HERE, adjacent to the control they
              disable, each with a jump to the section that fixes it. Classic
              flow keeps the current presentation until after the cert session
              (shared surface). Mounted for the whole confirm-flow step (R5/R6)
              — polite role from mount, list only while populated; note the
              ownership condition deliberately drops `nextDisabled`: on the
              confirm step blocked by the fee gate alone (no tab errors) the
              region must still be in the document with its role. */}
          {confirmFlowEnabled && !isLastTab && (
            <BlockingMessage
              surface="next-blockers"
              populated={currentTabErrors.length > 0}
              heading={<p className="font-semibold mb-1">Next is disabled until you fix:</p>}
              className="mb-3 p-3 rounded-lg border text-sm"
              style={{
                backgroundColor: '#FFFBEB',
                borderColor: '#FDE68A',
                color: '#92400E',
              }}
            >
              <ul className="list-disc list-inside space-y-0.5">
                {currentTabErrors.map((err) => {
                  const presentation = NEXT_BLOCKER_PRESENTATION[err.rule];
                  // SOR-160: the document-type control lives on each document's
                  // card (DocTypeCombobox with id `doc-type-combobox-<docId>`),
                  // so the ONE decider of which control a blocker names
                  // (R8's resolveOffendingFieldId — the doc-type leg resolves
                  // the first typeless document from wizardState, never by
                  // parsing the positional err.field) supplies the jump target;
                  // the static upload-section target is the fallback.
                  const resolvedFieldId = resolveOffendingFieldId(err, wizardState);
                  const scrollTarget = resolvedFieldId ?? presentation?.scrollTarget;
                  return (
                    <li key={`next-${err.field}-${err.rule}`} id={blockerItemId('next-blockers', err)}>
                      {scrollTarget ? (
                        <button
                          type="button"
                          className="underline text-left"
                          style={{ color: 'inherit' }}
                          onClick={() => {
                            document
                              .getElementById(scrollTarget)
                              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                        >
                          {err.message}
                        </button>
                      ) : (
                        err.message
                      )}
                      {presentation?.hint && (
                        <span className="block ml-5 text-xs">{presentation.hint}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </BlockingMessage>
          )}

          {/* Navigation buttons */}
          <div
            className="flex justify-between items-center pt-4 border-t"
            style={{ borderColor: brand.colors.border }}
          >
            {/* Back stays visually secondary (ghost) so each step has exactly
                one prominent forward action — Next/Submit (Walter R45). */}
            <button
              onClick={previousTab}
              disabled={isFirstTab}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{
                color: brand.colors.textLight,
                opacity: isFirstTab ? 0.5 : 1,
                cursor: isFirstTab ? 'not-allowed' : 'pointer',
              }}
            >
              &larr; Back
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  try {
                    if (!orderId) {
                      await createDraftOrder();
                    } else {
                      await saveDraftToServer();
                    }
                    saveDraft(); // Also save to localStorage
                  } catch {
                    // SOR-355: the provider recorded the outcome and the
                    // breadcrumb badge reports it. Swallowing here keeps a
                    // failed manual save from becoming an unhandled rejection.
                  }
                }}
                className="px-6 py-2 rounded-lg text-sm font-medium border transition-colors"
                style={{
                  borderColor: brand.colors.primary,
                  color: brand.colors.link,
                }}
              >
                Save As Draft
              </button>
            </div>

            {isLastTab ? (
              <div className="flex items-center gap-3">
                {/* Remaining fields indicator */}
                {completeness.percentage < 100 && (
                  <span
                    className="text-xs font-medium"
                    style={{ color: brand.colors.warning || '#F59E0B' }}
                  >
                    {completeness.totalFields - completeness.completedFields} required field{completeness.totalFields - completeness.completedFields !== 1 ? 's' : ''} remaining
                  </span>
                )}
                {/* R11 + the R76/R77 blocker discipline: a Submit disabled by
                    the section gate alone would read as a dead stall at 100%
                    completeness — the fields ARE all filled. Name the sections
                    still awaiting the filer's approval, adjacent to the control
                    they disable. */}
                {sectionsUnconfirmed && (
                  <span
                    data-testid="submit-sections-unconfirmed"
                    className="text-xs font-medium"
                    style={{ color: brand.colors.warning || '#F59E0B' }}
                  >
                    Confirm every section before submitting:{' '}
                    {SECTION_ORDER.filter(
                      s => getSectionStatus(wizardState, s) !== 'confirmed',
                    )
                      .map(s => SECTION_LABEL[s])
                      .join(', ')}
                  </span>
                )}
                {/* R12: the validator's own refusal, named next to the control
                    it disables. See `submitValidatorReasons` for why this is
                    scoped to the residue rather than to `!orderValid`. */}
                {submitValidatorReasons.length > 0 && (
                  <span
                    data-testid="submit-order-invalid"
                    className="text-xs font-medium"
                    style={{ color: brand.colors.warning || '#F59E0B' }}
                  >
                    {submitValidatorReasons.join(' ')}
                  </span>
                )}
                <button
                  data-testid="submit-order"
                  aria-disabled={submitDisabled || undefined}
                  onClick={async () => {
                    // R7: gated activation refuses — the reason goes in focus
                    // instead of a control that fires no events. The guard sits
                    // BEFORE the try, so a gated click never reaches
                    // submitOrder(); isSubmitting and submitOutcomeUnknown are
                    // submitDisabled terms, so double-submit protection is this
                    // guard now that the button no longer carries `disabled`.
                    if (submitDisabled) {
                      focusFirstBlockingMessage();
                      return;
                    }
                    try {
                      const result = await submitOrder();
                      clearDraft();
                      navigate(`/orders/${result.id}`);
                    } catch {
                      // submitError is already set in context — user stays on wizard to fix and retry.
                      // On an unknown outcome (submitOutcomeUnknown) the handler refuses instead.
                      // R15: the failure is announced from the banner and focus
                      // moves to it; that happens in the effect above, which
                      // reads the committed state rather than this continuation.
                    }
                  }}
                  className="px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: brand.colors.primary,
                    opacity: submitDisabled ? 0.6 : 1,
                    cursor: submitDisabled ? 'not-allowed' : 'pointer',
                    color: brand.colors.onPrimary,
                  }}
                >
                  {isSubmitting ? 'Submitting...' : !allUploadsComplete ? 'Uploading documents...' : 'Submit'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  // R7: gated activation refuses and puts the reason in focus
                  // instead — same guard shape as Submit's. Un-gated this is
                  // the plain nextTab() it always was.
                  if (nextDisabled) {
                    focusFirstBlockingMessage();
                    return;
                  }
                  nextTab();
                }}
                aria-disabled={nextDisabled || undefined}
                className="px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: brand.colors.primary,
                  opacity: nextDisabled ? 0.5 : 1,
                  cursor: nextDisabled ? 'not-allowed' : 'pointer',
                  color: brand.colors.onPrimary,
                }}
                title={nextDisabled ? 'Fix the errors above before continuing' : ''}
              >
                Next
              </button>
            )}
          </div>

          {/* Unknown submit outcome — the response was lost and the reconcile
              fetch could not reach the server. Submit refuses activation
              (R7): a second submit while the first may have landed could
              double-file. */}
          {submitOutcomeUnknown && (
            <div
              data-testid="submit-outcome-unknown"
              className="mt-3 p-3 rounded-lg text-sm"
              style={{
                backgroundColor: '#FFFBEB',
                color: '#B45309',
                border: '1px solid #FDE68A',
              }}
            >
              We couldn't confirm whether your filing was received — please do not submit it
              again. Check{' '}
              <button
                type="button"
                onClick={() => navigate('/orders')}
                className="underline font-medium"
                style={{ color: '#B45309' }}
              >
                My Orders
              </button>{' '}
              for this order's status, or refresh this page in a moment.
            </div>
          )}

        </div>

        {/* Summary Sidebar */}
        <div className="w-80 flex-shrink-0 hidden lg:block">
          <SummaryV2 brand={brand} />
        </div>
      </div>

      {/* Help Button */}
      <button
        onClick={() => setHelpOpen(true)}
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-xl font-bold transition-transform hover:scale-110 z-30"
        style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
        aria-label="Open help"
        data-testid="help-button"
      >
        ?
      </button>

      {/* Help Drawer */}
      <HelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} brand={brand} />
    </div>
  );
};

const EFilingWizard: React.FC<EFilingWizardProps> = ({ brand }) => {
  return (
    <EFilingProvider>
      <WizardContent brand={brand} />
    </EFilingProvider>
  );
};

export default EFilingWizard;
